import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { gradeApi } from '../utils/api';
import { GraduationCap, Calendar, BookOpen, Plus, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';

interface Grade {
  id: number;
  subject: string;
  score: number;
  created_at: string;
}

interface Registration {
  id: number;
  name: string;
  is_active: boolean;
}

export default function Grades() {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'teacher';
  const queryClient = useQueryClient();
  
  const [selectedReg, setSelectedReg] = useState<number | ''>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newRegName, setNewRegName] = useState('');

  // Get active registrations
  const { data: registrations } = useQuery({
    queryKey: ['gradeRegistrations'],
    queryFn: async () => {
      const response = await gradeApi.getRegistrations();
      return response.data.registrations as Registration[];
    },
  });

  // Get my grades or all grades if admin
  const { data: grades, isLoading: gradesLoading } = useQuery({
    queryKey: ['grades', selectedReg],
    queryFn: async () => {
      const response = await gradeApi.getGrades(selectedReg as number);
      return response.data.grades as Grade[];
    },
    enabled: !!selectedReg,
  });

  const createRegMutation = useMutation({
    mutationFn: (name: string) => gradeApi.createRegistration(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradeRegistrations'] });
      toast.success('成績登記期間已創建');
      setShowCreateForm(false);
      setNewRegName('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || '創建失敗');
    },
  });

  const handleCreateReg = (e: React.FormEvent) => {
    e.preventDefault();
    if (newRegName.trim()) {
      createRegMutation.mutate(newRegName.trim());
    }
  };

  const calculateAverage = () => {
    if (!grades?.length) return 0;
    const total = grades.reduce((sum: number, g: Grade) => sum + g.score, 0);
    return (total / grades.length).toFixed(1);
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    if (score >= 80) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 70) return 'text-amber-600 bg-amber-50 border-amber-200';
    if (score >= 60) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-rose-600 bg-rose-50 border-rose-200';
  };

  const getScoreBadge = (score: number) => {
    if (score >= 90) return { text: '優秀', class: 'bg-emerald-100 text-emerald-700' };
    if (score >= 80) return { text: '良好', class: 'bg-blue-100 text-blue-700' };
    if (score >= 70) return { text: '中等', class: 'bg-amber-100 text-amber-700' };
    if (score >= 60) return { text: '及格', class: 'bg-orange-100 text-orange-700' };
    return { text: '不及格', class: 'bg-rose-100 text-rose-700' };
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <GraduationCap className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">成績管理</h1>
            <p className="text-slate-500">查看和管理您的成績</p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl font-medium transition-all shadow-lg shadow-indigo-500/25"
          >
            <Plus className="h-4 w-4" />
            新增登記期間
          </button>
        )}
      </div>

      {/* Create Registration Form */}
      {showCreateForm && isAdmin && (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 mb-6 animate-slide-up">
          <h3 className="text-lg font-bold text-slate-900 mb-4">新增成績登記期間</h3>
          <form onSubmit={handleCreateReg} className="flex gap-3">
            <input
              type="text"
              className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              placeholder="例如：第一次段考、期末考..."
              value={newRegName}
              onChange={(e) => setNewRegName(e.target.value)}
              required
            />
            <button
              type="submit"
              disabled={createRegMutation.isPending}
              className="px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-xl transition-all disabled:opacity-50"
            >
              {createRegMutation.isPending ? '創建中...' : '創建'}
            </button>
          </form>
        </div>
      )}

      {/* Registration Selector */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5 text-slate-500" />
          <span className="font-semibold text-slate-700">選擇成績登記期間</span>
        </div>
        <div className="relative">
          <select
            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all appearance-none bg-white"
            value={selectedReg}
            onChange={(e) => setSelectedReg(e.target.value ? parseInt(e.target.value) : '')}
          >
            <option value="">請選擇登記期間...</option>
            {registrations?.map((reg) => (
              <option key={reg.id} value={reg.id}>
                {reg.name} {reg.is_active ? '(進行中)' : ''}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Grades Display */}
      {selectedReg && (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                {registrations?.find(r => r.id === selectedReg)?.name} 成績
              </h2>
            </div>
          </div>

          <div className="p-6">
            {gradesLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : grades?.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-slate-500">暫無成績資料</p>
                {isAdmin && (
                  <p className="text-sm text-slate-400 mt-2">
                    使用上方「新增登記期間」或聯繫管理員登記成績
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="grid gap-3">
                  {grades?.map((grade: Grade) => {
                    const badge = getScoreBadge(grade.score);
                    return (
                      <div
                        key={grade.id}
                        className={`flex items-center justify-between p-4 rounded-xl border ${getScoreColor(grade.score)}`}
                      >
                        <span className="font-semibold text-slate-900">{grade.subject}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl font-bold">{grade.score}</span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.class}`}>
                            {badge.text}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Statistics */}
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-500 mb-1">科目數</p>
                      <p className="text-2xl font-bold text-slate-900">{grades?.length || 0}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-500 mb-1">平均分</p>
                      <p className={`text-2xl font-bold ${getScoreColor(parseFloat(calculateAverage() || '0')).split(' ')[0]}`}>
                        {calculateAverage()}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-500 mb-1">總分</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {grades?.reduce((sum: number, g: Grade) => sum + g.score, 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
